import React, { FC, useState, useEffect } from 'react'
import { FormattedMessage } from 'react-intl'
import Input from '@vtex/styleguide/lib/Input'
import Textarea from '@vtex/styleguide/lib/Textarea'
import Button from '@vtex/styleguide/lib/Button'
import Modal from '@vtex/styleguide/lib/Modal'
import isEmpty from 'lodash/isEmpty'

import { logClickEvent } from '@/checkout-instore-legacy/utils/EventHelper'
import * as CustomEvent from '@/checkout-instore-legacy/utils/CustomEvent'
import { getInstoreConfig } from '@/checkout-instore-legacy/utils/configUtils'
import { useMediaQuery } from '@/checkout-instore-legacy/hooks/mediaQuery'
import { useDidUpdate } from '@/checkout-instore-legacy/hooks/useDidUpdate'
import {
  useOrderError,
  useOrderLoading,
  useOrderErrorActions,
} from '@/checkout-instore-legacy/order/hooks/root'
import { useOrderNoteActions } from '@/checkout-instore-legacy/order/hooks/note'

export type NoteProps = {
  value: string
  isOpen: boolean
  onClose: () => void
  isOrderNote: boolean
}

function useNoteLabelValues(isOrderNote: boolean) {
  const label = isOrderNote ? (
    <FormattedMessage id={'observationNote'} />
  ) : (
    <FormattedMessage id={'vendorObservationNote'} />
  )

  const helpText = isOrderNote ? (
    <FormattedMessage id="observationHelpText" />
  ) : (
    ''
  )

  const modalTitle = isOrderNote ? (
    <FormattedMessage id={'cartObservationTitle'} />
  ) : (
    <FormattedMessage id={'cartVendorTitle'} />
  )

  return {
    label,
    helpText,
    modalTitle,
  }
}

function useOrderNoteValue(
  initialValue: string
): [string, (value: string) => void] {
  const [note, setNote] = useState(initialValue)

  useDidUpdate(
    function onInitialValueChanged() {
      setNote(initialValue)
    },
    [initialValue]
  )

  return [note, setNote]
}

function useNoteState({ isOpen, value, onClose, isOrderNote }: NoteProps) {
  const initialValue = value || ''
  const [note, setNote] = useOrderNoteValue(initialValue)
  const [editing, setEditing] = useState(false)
  const error = useOrderError()
  const { message: errorMessage } = error || {}
  const loading = useOrderLoading()
  const { clearError } = useOrderErrorActions()
  const { saveNote, saveVendorCode } = useOrderNoteActions()
  const { noteAsVendorCode } = getInstoreConfig()

  const handleNoteChange = ({ target: { value } }: InputValueEvent) => {
    setNote(value)

    if (!isEmpty(error)) {
      clearError()
    }
  }

  const saveCurrentNoteValue = () => {
    if (isOrderNote) {
      saveNote(note, isOrderNote)
      CustomEvent.sendEvent('note.change', { note })
    } else {
      saveVendorCode(
        note,
        noteAsVendorCode?.skipValidation || false,
        noteAsVendorCode?.mask
      )
    }
  }

  const removeNoteValue = () => {
    if (isOrderNote) {
      saveNote('', isOrderNote)
    } else {
      saveNote('')
    }
  }

  useDidUpdate(
    function onExternalNoteChanged() {
      // This will be called when the external value was update (probably due to an action like saveNote + request succesfully done)
      if (isOpen) {
        clearError()
        onClose()
      }
    },
    [initialValue]
  )

  useEffect(
    function onModalClose() {
      if (!isOpen) {
        clearError()
        setEditing(false)
      }
    },
    [clearError, setEditing, isOpen]
  )

  return {
    note,
    errorMessage,
    loading,
    editing,
    setEditing,
    saveCurrentNoteValue,
    removeNoteValue,
    handleNoteChange,
    type: noteAsVendorCode?.type,
  }
}

const Note: FC<NoteProps> = ({ isOpen, value, onClose, isOrderNote }) => {
  const {
    note,
    errorMessage,
    loading,
    editing,
    removeNoteValue,
    saveCurrentNoteValue,
    setEditing,
    handleNoteChange,
    type,
  } = useNoteState({ isOpen, value, onClose, isOrderNote })

  const { label, helpText, modalTitle } = useNoteLabelValues(isOrderNote)

  const { value: isDesktop } = useMediaQuery({
    phone: false,
    tablet: false,
    desktop: true,
  })

  const Inputs = {
    text: Textarea,
    textarea: Textarea,
    input: Input,
    select: Textarea,
  }

  const InputComponent = type ? Inputs[type] : Textarea

  useEffect(
    function notifyVisible() {
      if (isOpen) {
        CustomEvent.sendEvent('note.visible')
      }
    },
    [isOpen]
  )

  const handleFormSubmit = async (e: any) => {
    e.preventDefault()

    logClickEvent('submit-oservation-note', 'Sale')

    saveCurrentNoteValue()
  }

  const handleRemove = () => {
    removeNoteValue()
    CustomEvent.sendEvent('note.removed', { note: null })
  }

  const trimmedNote = note.trim()

  return (
    <Modal centered title={modalTitle} isOpen={isOpen} onClose={onClose}>
      <div data-testid="note-form-modal-container" className="nb5">
        {!value || editing ? (
          <form data-testid="note-form" onSubmit={handleFormSubmit}>
            <InputComponent
              value={note}
              onChange={handleNoteChange}
              label={label}
              helpText={helpText}
              error={!!errorMessage}
              errorMessage={errorMessage}
              dataAttributes={{
                testid: 'note-input',
              }}
            />
            <div className="flex justify-end mt6">
              <Button
                testId="note-submit"
                type="submit"
                variation="primary"
                isLoading={loading}
                disabled={!trimmedNote || note === value}
                block={!isDesktop}
              >
                {value ? (
                  <FormattedMessage id="noteConfirm" />
                ) : (
                  <FormattedMessage id="noteAdd" />
                )}
              </Button>
            </div>
          </form>
        ) : (
          <div>
            <InputComponent
              value={note}
              label={label}
              helpText={helpText}
              disabled
              dataAttributes={{
                testid: 'note-input',
              }}
            />
            <div className="flex justify-end mt6">
              <div className={`flex ${!isDesktop ? 'flex-auto' : ''}`}>
                <Button onClick={() => setEditing(true)} block={!isDesktop}>
                  <FormattedMessage id="noteUpdate" />
                </Button>
              </div>
              <div className={`flex ml5 ${!isDesktop ? 'flex-auto' : ''}`}>
                <Button
                  variation="danger"
                  isLoading={loading}
                  onClick={handleRemove}
                  block={!isDesktop}
                >
                  <FormattedMessage id="noteRemove" />
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Modal>
  )
}

const NoteMemoized = React.memo(Note)

export { NoteMemoized as Note }
